import { useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import MyTextField from "../../components/inputs/MyTextField";
import MyButton from "../../components/MyButton";
import { useAuth } from "../../hooks/useAuth";
import { SiteContext } from "../../context/SiteProvider";

const ForgotPassword = () => {
  const { findPasswordByEmailMutation, resetPasswordByEmailMutation } =
    useAuth();
  const { setLoadingScreen } = useContext(SiteContext);
  const navigate = useNavigate();

  const initValues = {
    userId: "",
    email: "",
    isSentOtp: false,
    password: "",
    rePassword: "",
    isConfirmed: false,
  };

  const validationSchema = yup.object({
    userId: yup.string().required("필수 입력 항목입니다."),
    email: yup
      .string()
      .email("이메일 형식이 올바르지 않습니다.")
      .required("이메일을 입력해 주세요."),
    otp: yup.string().required("필수 입력 항목입니다."),
    password: yup.string().required("이메일을 입력해 주세요."),
    rePassword: yup
      .string()
      .required("이메일을 입력해 주세요.")
      .oneOf([yup.ref("password")], "비밀번호가 일치하지 않습니다."),
  });
  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const isSentOtp = methods.watch("isSentOtp");
  const isConfirmed = methods.watch("isConfirmed");
  const email = methods.watch("email");

  const onVerifyEmail = async () => {
    const isValidUserId = await methods.trigger("userId");
    const isValidEmail = await methods.trigger("email");

    if (isValidUserId && isValidEmail) {
      methods.clearErrors("isSentOtp");

      const payload = {
        id: methods.getValues("userId"),
        email: methods.getValues("email"),
      };

      setLoadingScreen(true);

      findPasswordByEmailMutation.mutate(payload, {
        onSuccess: (res) => {
          methods.clearErrors("email");
          methods.setValue("userToken", res.userToken);
          methods.setValue("otp", "");
          methods.setValue("isSentOtp", true);
          methods.trigger("otp");
        },
        onError: (error) => {
          if (error.data.code === "NOT_FOUND") {
            methods.setError("email", {
              message: "사용자가 존재하지 않습니다.",
            });
          } else {
            alert("검색 중 오류가 발생했습니다.");
          }
        },
        onSettled: () => {
          setLoadingScreen(false);
        },
      });
    }
  };

  const onSubmit = (data) => {
    const payload = {
      userToken: data.userToken,
      pincode: data.otp,
      password: data.password,
    };

    setLoadingScreen(true);

    resetPasswordByEmailMutation.mutate(payload, {
      onSuccess: async (res) => {
        if (res?.accessToken) {
          methods.clearErrors("otp");
          methods.setValue("isConfirmed", true);
          alert("확인 완료되었습니다.");
        } else {
          methods.setError("otp", {
            message: "비밀번호 변경에 실패했습니다.",
          });
          alert("비밀번호 변경에 실패했습니다.");
        }
      },
      onError: () => {
        alert("비밀번호 변경에 실패했습니다.");
      },
      onSettled: () => {
        setLoadingScreen(false);
      },
    });
  };

  return (
    <div
      className="flex items-center bg-cover bg-right-top bg-no-repeat login-page md:py-6 py-3"
      style={{
        backgroundImage: "url(/assets/img/logbg.png)",
        minHeight: "calc(100vh - 182px)",
      }}
    >
      <div className="container flex justify-center">
        <div className="max-w-[470px] w-full">
          <h2 className="mb-1 font-bold lg:text-4xl sm:text-3xl text-xl text-center">
            비밀번호 재설정
          </h2>

          {!isConfirmed && (
            <p className="md:text-lg md:mb-10 mb-6 text-center whitespace-pre-wrap">
              {!isSentOtp ? (
                "비밀번호를 잊어버리셨나요?\n재설정을 위한 인증번호 메일을 보내드립니다."
              ) : (
                <>
                  <b className="md:text-lg">{email}</b> 로 인증번호가
                  발송되었습니다.
                </>
              )}
            </p>
          )}

          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              {isSentOtp && !isConfirmed ? (
                <>
                  <div className="flex flex-wrap md:mb-5 mb-3">
                    <label
                      htmlFor="otp"
                      className="md:mt-4 md:w-[100px] w-full font-semibold"
                    >
                      인증번호
                    </label>
                    <div className="flex-1">
                      <MyTextField
                        id="otp"
                        name="otp"
                        placeholder="인증번호를 입력해주세요."
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap md:mb-5 mb-3">
                    <label
                      htmlFor="password"
                      className="md:mt-4 md:w-[100px] w-full font-semibold"
                    >
                      비밀번호
                    </label>
                    <div className="flex-1">
                      <MyTextField
                        id="password"
                        type="password"
                        name="password"
                        placeholder="새 비밀번호*"
                        hint="영문, 숫자, 특수문자를 포함하여 6자리 이상, 16자리 미만으로 입력해주세요."
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap md:mb-5 mb-3">
                    <label
                      htmlFor="password"
                      className="md:mt-4 md:w-[100px] w-full font-semibold"
                    >
                      비밀번호 확인
                    </label>
                    <div className="flex-1">
                      <MyTextField
                        id="rePassword"
                        type="password"
                        name="rePassword"
                        placeholder="새 비밀번호 확인*"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap md:mt-5 mt-3 gap-4 gap-y-4">
                    <MyButton
                      variant="outlined"
                      className="flex-1 sm:min-w-[48%] min-w-full h-[56px]"
                      size="small"
                      onClick={() => navigate("/Login")}
                    >
                      <span className="text-sm">다음에 변경하기</span>
                    </MyButton>
                    <MyButton
                      variant="contained"
                      color="primary"
                      className="flex-1 sm:min-w-[48%] font-semibold text-sm min-w-full h-[56px]"
                      size="small"
                      type="submit"
                    >
                      비밀번호 변경하기
                    </MyButton>
                  </div>
                </>
              ) : isSentOtp && isConfirmed ? (
                <>
                  <p className="md:text-lg md:mb-10 mb-6 text-center whitespace-pre-wrap">
                    비밀번호가 정상적으로 변경되었습니다
                  </p>
                  <div className="flex flex-wrap md:mt-5 mt-3 gap-4 gap-y-4">
                    <MyButton
                      variant="outlined"
                      className="flex-1 sm:min-w-[48%] min-w-full h-[56px]"
                      size="small"
                      onClick={() => navigate("/Login")}
                    >
                      <span className="text-sm">홈으로</span>
                    </MyButton>
                    <MyButton
                      variant="contained"
                      color="primary"
                      className="flex-1 sm:min-w-[48%] font-semibold text-sm min-w-full h-[56px]"
                      size="small"
                      onClick={() => navigate("/")}
                    >
                      로그인
                    </MyButton>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-wrap md:mb-5 mb-3">
                    <label
                      htmlFor="userId"
                      className="md:mt-4 md:w-[100px] w-full font-semibold"
                    >
                      아이디
                    </label>
                    <div className="flex-1">
                      <MyTextField
                        id="userId"
                        name="userId"
                        placeholder="회원가입 시 입력하신 아이디를 입력해주세요."
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap md:mb-5 mb-3">
                    <label
                      htmlFor="email"
                      className="md:mt-4 md:w-[100px] w-full font-semibold"
                    >
                      이메일
                    </label>
                    <div className="flex-1">
                      <MyTextField
                        id="email"
                        name="email"
                        placeholder="회원가입 시 입력하신 이메일 주소를 입력해주세요."
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap md:mt-5 mt-3 gap-4 gap-y-4">
                    <MyButton
                      variant="outlined"
                      className="flex-1 sm:min-w-[48%] min-w-full h-[56px]"
                      size="small"
                      onClick={() => navigate("/Login")}
                    >
                      <span className="text-sm">취소</span>
                    </MyButton>
                    <MyButton
                      variant="contained"
                      color="primary"
                      className="flex-1 sm:min-w-[48%] font-semibold min-w-full h-[56px] text-sm"
                      size="small"
                      onClick={onVerifyEmail}
                    >
                      변경 인증 메일 발송
                    </MyButton>
                  </div>
                </>
              )}
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
